import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DatePicker, Descriptions, Input, Modal, Popover, Select, Table, TimePicker } from 'antd';
import { DescriptionsItemProps } from 'antd/es/descriptions/Item';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import * as classAPI from '@apis/invoice';
import * as paymentStatementAPI from '@apis/paymentStatements';
import * as productAPI from '@apis/product';
import * as saleChannelsOrderAPI from '@apis/sales-channels';
import { useAuthentication } from '@hooks/authentication';
import { useViewport } from '@hooks/viewport';
import {
  ClassChangePurchaseItem,
  CommerceChangePurchasesItem,
  PaymentStatementPurchase,
} from '@models/paymentStatement';
import { ProductOption } from '@models/product';
import { SalesChannelsType } from '@models/sales-channels';
import { ClassSchedule } from '@models/schedule';
import { PARTNER_PURCHASES_QUERY_KEY } from '@pages/SalesHistoryPage';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import useChangeScheduleModalStore from '@stores/useChangeScheduleModalStore';
import { getChannelText } from '@utils/common';
import { combinedRefundItemsByName, extractDateFromStrings } from '@utils/payment';
import './ChangeScheduleModal.scss';

interface ChangeClassScheduleTableProps {
  classInvoiceId: string;
  result?: ClassChangePurchaseItem[];
  onChange: (data: ClassChangePurchaseItem[]) => void;
}
interface ChangeCommerceOptionTableProps {
  channel?: SalesChannelsType;
  productId: string;
  purchases: PaymentStatementPurchase[];
  paidAt: Date;
  result?: CommerceChangePurchasesItem[];
  onChange: (data: CommerceChangePurchasesItem[]) => void;
}

const ChangeClassScheduleTable: React.FC<ChangeClassScheduleTableProps> = (props) => {
  const { classInvoiceId, result, onChange } = props;

  const scheduleQuery = useQuery({
    queryKey: [`schedule-${classInvoiceId}`],
    queryFn: () => classAPI.getClassSchedulesByClassInvoiceId(classInvoiceId),
  });

  useEffect(() => {
    const data = scheduleQuery.data?.data;

    if (data && data.length !== 0) {
      onChange(Array.from({ length: data.length }, (_, index) => ({ currentItem: data[index], newItem: '' })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleQuery.data?.data]);

  const handleTimeChange = (time: string, index: number, type: 'date' | 'time', dateRegex: RegExp) => {
    if (!time || !result) return;

    if (dateRegex.test(result[index].newItem)) {
      const newResult = [...result];
      newResult[index].newItem = newResult[index].newItem.replace(dateRegex, time);

      onChange(newResult);
    } else {
      const newResult = [...result];

      if (newResult[index].newItem) {
        if (type === 'date') {
          newResult[index].newItem = `${time} ${newResult[index].newItem}`;
        } else {
          newResult[index].newItem = `${newResult[index].newItem} ${time}`;
        }
      } else {
        newResult[index].newItem = moment(newResult[index].currentItem.startDate).format('YYYY-MM-DD HH:mm');
        newResult[index].newItem = newResult[index].newItem.replace(dateRegex, time);
      }

      onChange(newResult);
    }
  };

  const CLASS_SCHEDULE_COLUMNS: ColumnsType<ClassSchedule> = [
    {
      width: 300,
      title: '수업 일시',
      align: 'center',
      dataIndex: 'startDate',
      render: (startDate) => moment(startDate).format('YYYY. MM. DD. HH:mm'),
    },
    {
      title: '변경 수업 일시',
      align: 'center',
      children: [
        {
          width: 200,
          title: '날짜',
          align: 'center',
          dataIndex: 'startDate',
          render: (startDate: string, _, index) => {
            const regex = /\d{4}-\d{1,2}-\d{1,2}/;
            const date = result?.[index]?.newItem.match(regex) || moment(startDate).format().match(regex) || [];

            return (
              <DatePicker
                value={dayjs(date[0])}
                onChange={(e) => handleTimeChange(e?.format('YYYY-MM-DD') || '', index, 'date', regex)}
              />
            );
          },
        },
        {
          width: 150,
          title: '시간',
          align: 'center',
          dataIndex: 'startDate',
          render: (startDate: string, _, index) => {
            const regex = /T?\d{1,2}:\d{1,2}/;
            const date = result?.[index]?.newItem.match(regex) || moment(startDate).format().match(regex) || [];

            return (
              <TimePicker
                value={dayjs(date[0]?.replace('T', '') || '00:00', 'HH:mm')}
                format="HH:mm"
                minuteStep={5}
                onChange={(e) => handleTimeChange(e?.format('HH:mm') || '', index, 'time', regex)}
              />
            );
          },
        },
      ],
    },
  ];

  return (
    <Table
      title={() => '수업 일정 목록'}
      pagination={false}
      bordered
      columns={CLASS_SCHEDULE_COLUMNS}
      dataSource={scheduleQuery.data?.data}
    />
  );
};

const ChangeCommerceOptionTable: React.FC<ChangeCommerceOptionTableProps> = (props) => {
  const { productId, purchases, result, onChange } = props;
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const customInputRef = useRef<any>(null);
  const dialog = useOkCancelDialog();

  const productOptionQuery = useQuery({
    queryKey: [`product-options-${productId}`],
    queryFn: () => productAPI.getProductOptions(productId),
  });

  useEffect(() => {
    if (productOptionQuery.data?.data && productOptionQuery.data?.data?.length > 0) {
      setProductOptions(productOptionQuery.data?.data);
    }
  }, [productOptionQuery.data?.data]);

  useEffect(() => {
    onChange(
      Array.from({ length: purchases.length }, (_, index) => ({
        currentItem: purchases[index],
        newItem: '',
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchases]);

  const handleAddButton = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setProductOptions((prev) => {
      const newValue = [...prev];
      const inputValue = customInputRef.current?.input?.value.trim();
      if (inputValue) {
        const checkIsExist = newValue.some((item) => {
          if (item.step.join(' / ') === inputValue) {
            return true;
          }
          return false;
        });
        if (!checkIsExist) {
          const customOption: ProductOption = {
            step: [inputValue],
            inventory: 0,
            fee: 0,
          };
          return [customOption, ...newValue];
        }
      }
      return newValue;
    });
  };

  const handleSelectChange = (changeProductOption: ProductOption, currentProductOption: string, uid?: string) => {
    if (!result) return;
    const newResult = [...result];
    const newOptionName = changeProductOption?.step.join(' / ');

    result.forEach((item, index) => {
      if (uid) {
        if (uid === item?.currentItem?.uid) {
          newResult[index].newItem = newOptionName;
        }
      } else {
        if (item.currentItem.name === currentProductOption) {
          newResult[index].newItem = newOptionName;
        }
      }
    });

    onChange(newResult);
  };

  const COMMERCE_SCHEDULE_COLUMS: ColumnsType<PaymentStatementPurchase> = [
    {
      width: 300,
      title: '수업 일시',
      align: 'center',
      render: (record) => record.name,
    },
    {
      title: '변경할 수업 일시',
      align: 'center',
      render: (record, _record, index) => {
        const isRequiredOption = record?.required;
        return (
          <Select
            disabled={isRequiredOption}
            defaultValue={isRequiredOption ? '필수옵션 변경불가' : result?.[index]?.newItem || ''}
            options={productOptions.map((item) => ({
              value: JSON.stringify(item),
              label: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <div>{item.step.join(' / ')}</div>
                  <div style={{ fontWeight: 'bold' }}>(재고: {item?.inventory || 0})</div>
                </div>
              ),
            }))}
            popupMatchSelectWidth={false}
            style={{ minWidth: 250 }}
            onChange={(e) => handleSelectChange(JSON.parse(e), record.name, record?.uid)}
            dropdownRender={(option) => (
              <Fragment>
                {option}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Popover
                    content={
                      <div style={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}>
                        <span style={{ color: 'red', fontWeight: 'bold' }}>'12/3(월) / 15:00'</span>과 같은 일정 형식이
                        포함되어야{'\n'}변경 후 수업일로 검색하실 수 있습니다.
                      </div>
                    }>
                    <Input
                      placeholder="커스텀 옵션 추가"
                      defaultValue={customInputRef?.current?.input?.value || ''}
                      ref={customInputRef}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </Popover>
                  <button
                    className="pa-button black ws hs"
                    onClick={(e) => {
                      const newOptionName = customInputRef.current?.input?.value.trim();
                      const { matchDays } = extractDateFromStrings([newOptionName]);
                      if (matchDays.length < 1) {
                        dialog.open({
                          title: '권장되는 날짜 형식이 아닙니다.',
                          content:
                            '권장 형식이 아닐경우, 이후 일정검색에서 검색되지 않습니다.\n[ 권장형식: "4/11(월) / 11:30" 또는 "4월 11일 / 11:30" ]\n추가하시겠습니까?',
                          type: 'ok_cancel',
                          onConfirm: () => {
                            handleAddButton(e);
                          },
                        });
                        return;
                      }
                      handleAddButton(e);
                    }}>
                    추가
                  </button>
                </div>
              </Fragment>
            )}
          />
        );
      },
    },
  ];

  return (
    <Table
      title={() => '수업 일정 목록'}
      pagination={false}
      bordered
      columns={COMMERCE_SCHEDULE_COLUMS}
      dataSource={purchases}
    />
  );
};

const ChangeScheduleModal: React.FC = () => {
  const { data, result, show, close, onChange } = useChangeScheduleModalStore();
  const { authStore } = useAuthentication();
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const combinedRefundItems = combinedRefundItemsByName(data?.refunds);
  const { isMobile } = useViewport();

  const updatePaymentStatementScheduleMutation = useMutation({
    mutationFn: paymentStatementAPI.changeSchedule,
    onSuccess: async () => {
      close();
      dialog.open({
        type: 'ok',
        title: '일정 변경',
        content: '일정 변경에 성공했습니다.',
      });
      await queryClient.invalidateQueries({
        queryKey: [`schedule-${data?.classInvoiceId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [PARTNER_PURCHASES_QUERY_KEY(authStore.user)],
      });
      await queryClient.invalidateQueries({
        queryKey: [`product-options-${data?.productId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [`product-orders-${authStore?.user?.id}`],
      });
    },
    onError: (error: AxiosError<{ error: string }>) => {
      let errorContent = '일정 변경에 실패했습니다.';

      if (error.response?.data?.error === 'NOT_ENOUGH_INVENTORY') {
        errorContent = '바꾸고자 하는 상품의 재고가 부족합니다.';
      }

      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '일정 변경',
        content: errorContent,
      });
    },
  });

  const updateSalesChannelOrderScheduleMutation = useMutation({
    mutationFn: saleChannelsOrderAPI.changePlannedDate,
    onSuccess: async () => {
      close();
      dialog.open({
        type: 'ok',
        title: '일정 변경',
        content: '일정 변경에 성공했습니다.',
      });
      await queryClient.invalidateQueries({
        queryKey: [`schedule-${data?.classInvoiceId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [PARTNER_PURCHASES_QUERY_KEY(authStore.user)],
      });
      await queryClient.invalidateQueries({
        queryKey: [`product-options-${data?.productId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: [`product-orders-${authStore?.user?.id}`],
      });
    },
    onError: (e: any) => {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '변경 실패',
        content: `${e?.response?.data?.message}`,
      });
    },
  });

  const isSubmitting =
    updatePaymentStatementScheduleMutation.isPending || updateSalesChannelOrderScheduleMutation.isPending;

  const handleConfirmClick = () => {
    if (isSubmitting) {
      return;
    }

    if (data) {
      const channel = data?.channel;
      const requestBody: paymentStatementAPI.ChangeScheduleBody = {
        body: {
          saleType: data.saleType === 'class' ? 'class' : 'commerce',
          changePurchaseItems: [],
          paidAt: data.paidAt,
          productId: data?.productId,
        },
      };

      if (result && result.length) {
        if (data!.saleType === 'class') {
          requestBody.body.changePurchaseItems = (result as ClassChangePurchaseItem[]).filter((item) => item.newItem);
        } else {
          requestBody.body.changePurchaseItems = (result as CommerceChangePurchasesItem[]).filter(
            (item) => item.newItem,
          );
        }
      }

      if (channel) {
        requestBody['salesChannelOrderId'] = data?.orderId;
        updateSalesChannelOrderScheduleMutation.mutate(requestBody);
      } else {
        requestBody['paymentStatementId'] = data?.orderId;
        updatePaymentStatementScheduleMutation.mutate(requestBody);
      }
    } else {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '일정 변경',
        content: '일정 변경에 실패했습니다\n새로고침 후 다시 시도해주세요.',
      });
    }
  };

  const PURCHASES_INFO_ITEMS: DescriptionsItemProps[] = [
    { span: 3, label: '판매채널', children: getChannelText(data?.channel) },
    { span: 3, label: '상품명', children: data?.productName || '-' },
    { span: 3, label: '구매자', children: data?.buyerName || '-' },
    {
      span: 3,
      label: '구매옵션',
      children: data?.purchases?.map((item) => <div>{`${item.name} (${item.count})`}</div>) || '-',
    },
    {
      span: 3,
      label: '입력옵션',
      children: data?.memo?.qna || '-',
    },
    {
      span: 3,
      label: '요청사항',
      children: data?.memo?.payment || '-',
    },
    {
      span: 3,
      label: '취소옵션',
      children: combinedRefundItems.map((item) => <div>{`${item.name} (${item.count})`}</div>) || '-',
    },
    { span: 3, label: '결제금액', children: `${data?.paidAmount.toLocaleString() || 0}원` },
  ];

  if (!show) {
    return <div></div>;
  }

  return (
    <Modal
      className="schedule-modal"
      title="일정 변경"
      open={show}
      width={800}
      onCancel={close}
      footer={null}
      centered
      styles={{ content: { height: isMobile ? '100vh' : '95vh' } }}>
      <div className="schedule-modal__content">
        <Descriptions
          title="구매 정보"
          bordered
          items={PURCHASES_INFO_ITEMS}
          labelStyle={{ alignContent: 'center' }}
          style={{ whiteSpace: 'pre-wrap' }}
        />
        {data && data.classInvoiceId && data.saleType === 'class' && (
          <ChangeClassScheduleTable
            classInvoiceId={data.classInvoiceId}
            result={result as ClassChangePurchaseItem[]}
            onChange={onChange}
          />
        )}
        {data && data.saleType === 'commerce' && (
          <ChangeCommerceOptionTable
            channel={data?.channel}
            productId={data.productId}
            purchases={data.remainPurchases}
            paidAt={data.paidAt}
            result={result as CommerceChangePurchasesItem[]}
            onChange={onChange}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '24px 0' }}>
        <button className="pa-button black wxl hm" disabled={isSubmitting} onClick={() => handleConfirmClick()}>
          {isSubmitting ? '변경 중...' : '변경하기'}
        </button>
        <button className="pa-button white wxl hm" disabled={isSubmitting} onClick={close}>
          닫기
        </button>
      </div>
    </Modal>
  );
};

export default ChangeScheduleModal;
